import { type ReactElement } from 'react';
import { CopyToClipboardButton } from '@/components/shared/CopyToClipboardButton';
import { API_BASE } from '@/lib/api-client';
import { useOrganization } from '@/hooks/api/useOrganizations';

interface PluginConfigDetailsProps {
  organizationId: string;
  className?: string;
}

interface DetailRowProps {
  label: string;
  value: string;
  isLoading?: boolean;
}

function DetailRow({ label, value, isLoading }: DetailRowProps): ReactElement {
  const hasValue = !isLoading && value.length > 0;
  return (
    <div className="flex items-start justify-between gap-3 py-1.5">
      <div className="min-w-0 flex-1">
        <div className="text-xs font-medium text-muted-foreground">{label}</div>
        <div className="font-mono text-sm break-all">
          {isLoading ? (
            <span className="text-muted-foreground">Loading…</span>
          ) : value.length > 0 ? (
            value
          ) : (
            <span className="italic text-muted-foreground">Unavailable</span>
          )}
        </div>
      </div>
      {hasValue && (
        <CopyToClipboardButton
          text={value}
          size="sm"
          variant="ghost"
          compactCopiedState
          aria-label={`Copy ${label.toLowerCase()}`}
        >
          <span className="sr-only">Copy {label.toLowerCase()}</span>
        </CopyToClipboardButton>
      )}
    </div>
  );
}

/**
 * Read-only summary of the values that get baked into the Claude Desktop
 * plugin zip when "Download Plugin" is clicked: the backend URL the plugin
 * will talk to, the organization name, and the organization id (UUID).
 *
 * Shown both on Get Started → Claude Desktop and in the Global Organization
 * Plugin API Keys card so the user can verify the destination before
 * distributing the plugin to a fleet.
 */
export function PluginConfigDetails({
  organizationId,
  className,
}: PluginConfigDetailsProps): ReactElement {
  const { data: organization, isLoading } = useOrganization(organizationId);

  return (
    <div
      className={`rounded-md border bg-muted/30 px-3 py-2 ${className ?? ''}`}
      data-testid="plugin-config-details"
    >
      <DetailRow label="Backend URL" value={API_BASE} />
      <DetailRow
        label="Organization name"
        value={organization?.name ?? ''}
        isLoading={isLoading}
      />
      <DetailRow label="Organization ID" value={organizationId} />
    </div>
  );
}
